import { CalendarCheck2, ClipboardList } from "lucide-react"
import { useDeferredValue, useMemo } from "react"

import type { AbsenceRequest } from "@/lib/absence-tracking-api"

import { AbsenceStateBadge } from "@/components/shared/absence-state-badge"
import { EmptyState } from "@/components/shared/empty-state"
import { FadeIn } from "@/components/shared/fade-in"
import {
  type ColumnDef,
  ResponsiveTable,
} from "@/components/shared/responsive-table"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import {
  formatGuildDateRange,
  formatGuildDateTime,
} from "@/lib/guild-date-format"
import { memberDisplayLabel } from "@/lib/member-display-label"
import {
  type AbsenceReviewUiStore,
  type ActiveSortOption,
  useReviewActiveSort,
  useReviewQueueFilter,
  useReviewSearchQuery,
} from "@/lib/stores/absence-review-ui-store"

export function ActiveAbsencesCard({
  store,
  activeAbsences,
}: {
  store: AbsenceReviewUiStore
  activeAbsences: AbsenceRequest[]
}) {
  const searchQuery = useReviewSearchQuery(store)
  const queueFilter = useReviewQueueFilter(store)
  const activeSort = useReviewActiveSort(store)

  const deferredSearchQuery = useDeferredValue(searchQuery)
  const normalizedSearch = deferredSearchQuery.trim().toLowerCase()

  const filteredActiveAbsences = useMemo(() => {
    if (!normalizedSearch) return activeAbsences
    return activeAbsences.filter((item) =>
      [item.member?.displayName, item.memberId, item.reason]
        .filter((value): value is string => Boolean(value))
        .some((value) => value.toLowerCase().includes(normalizedSearch)),
    )
  }, [normalizedSearch, activeAbsences])

  const sortedActiveAbsences = useMemo(() => {
    const items = [...filteredActiveAbsences]
    if (activeSort === "member") {
      items.sort((a, b) =>
        memberDisplayLabel({
          displayName: a.member?.displayName,
          memberId: a.memberId,
        }).localeCompare(
          memberDisplayLabel({
            displayName: b.member?.displayName,
            memberId: b.memberId,
          }),
        ),
      )
      return items
    }
    const direction = activeSort === "range_asc" ? 1 : -1
    items.sort(
      (a, b) =>
        (new Date(a.startAt).getTime() - new Date(b.startAt).getTime()) *
        direction,
    )
    return items
  }, [activeSort, filteredActiveAbsences])

  if (queueFilter !== "all" && queueFilter !== "approved") {
    return null
  }

  return (
    <FadeIn delay={0.07}>
      <Card>
        <CardHeader>
          <CardTitle>Active absences</CardTitle>
          <CardDescription>
            Future and current absences that already count against availability.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <ActiveAbsenceTable items={sortedActiveAbsences} />
        </CardContent>
      </Card>
    </FadeIn>
  )
}

const activeColumns: ColumnDef<AbsenceRequest>[] = [
  {
    header: "Member",
    isRowHeader: true,
    cell: (item) => (
      <div>
        {memberDisplayLabel({
          displayName: item.member?.displayName,
          memberId: item.memberId,
        })}
        <div className="text-xs font-normal text-muted-foreground">
          {item.memberId}
        </div>
      </div>
    ),
    mobileCard: (item) => (
      <div>
        <div className="text-sm font-semibold text-foreground">
          {memberDisplayLabel({
            displayName: item.member?.displayName,
            memberId: item.memberId,
          })}
        </div>
        <div className="text-xs text-muted-foreground">{item.memberId}</div>
      </div>
    ),
  },
  {
    header: "Range",
    cell: (item) => (
      <div className="flex items-center gap-2">
        <ClipboardList className="size-4 text-muted-foreground" />
        {formatGuildDateRange(item.startAt, item.endAt)}
      </div>
    ),
    mobileCard: (item) => (
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <ClipboardList className="size-4" />
        <span>{formatGuildDateRange(item.startAt, item.endAt)}</span>
      </div>
    ),
  },
  {
    header: "Reason",
    cell: (item) => (
      <span
        className="block max-w-48 truncate"
        title={item.reason ?? undefined}
      >
        {item.reason ?? "-"}
      </span>
    ),
    mobileCard: (item) => (
      <div className="text-sm">
        <span className="text-muted-foreground">Reason: </span>
        <span className="line-clamp-2">{item.reason ?? "-"}</span>
      </div>
    ),
  },
  {
    header: "Confirmed",
    cell: (item) =>
      item.reviewedAt ? formatGuildDateTime(item.reviewedAt) : "-",
    cellClassName: "text-muted-foreground",
    mobileCard: (item) => (
      <div className="flex items-center justify-between gap-2">
        <span className="text-xs text-muted-foreground">
          Confirmed{" "}
          {item.reviewedAt ? formatGuildDateTime(item.reviewedAt) : "-"}
        </span>
        <AbsenceStateBadge state={item.state} />
      </div>
    ),
  },
  {
    header: "Status",
    cell: (item) => <AbsenceStateBadge state={item.state} />,
  },
]

function ActiveAbsenceTable({ items }: { items: AbsenceRequest[] }) {
  return (
    <ResponsiveTable
      columns={activeColumns}
      data={items}
      keyFn={(item) => item.id}
      caption="Active absences"
      emptyState={
        <EmptyState
          icon={<CalendarCheck2 className="size-4" />}
          tone="success"
          title="No active absences"
          description="Coverage is currently clear for upcoming windows."
        />
      }
      mobileCardClassName="gap-2.5"
    />
  )
}
